/**
 * Header behavior:
 * - Toggle mobile menu on small viewports via [data-nav-open] on the header
 * - Wire cart-toggle button to dispatch 'meera:cart:open'
 * - Update cart count when 'meera:cart:updated' fires
 */
export function initHeader(): void {
  const headerEl = document.querySelector<HTMLElement>('[data-site-header]')
  const menuToggle = document.querySelector<HTMLButtonElement>('[data-menu-toggle]')
  if (headerEl && menuToggle) {
    const setOpen = (open: boolean) => {
      headerEl.dataset.navOpen = String(open)
      menuToggle.setAttribute('aria-expanded', String(open))
      menuToggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu')
    }
    menuToggle.addEventListener('click', () => {
      setOpen(headerEl.dataset.navOpen !== 'true')
    })
    // Close on viewport switching back to desktop
    const mq = window.matchMedia('(min-width: 769px)')
    mq.addEventListener('change', (e) => { if (e.matches) setOpen(false) })
    // Close on outbound nav link click
    headerEl.querySelectorAll<HTMLAnchorElement>('.site-header__nav a').forEach((a) => {
      a.addEventListener('click', () => setOpen(false))
    })
  }

  // Desktop dropdown menus: CSS reveals them on :focus-within, but we also need
  // to sync aria-expanded so screen readers know the state, and wire keyboard
  // shortcuts (ArrowDown to enter the menu, Escape to leave it).
  document.querySelectorAll<HTMLElement>('.site-header__nav-item.has-dropdown').forEach((item) => {
    const trigger = item.querySelector<HTMLAnchorElement>('.site-header__nav-link')
    const menuItems = Array.from(item.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]'))
    if (!trigger || menuItems.length === 0) return

    item.addEventListener('focusin', () => trigger.setAttribute('aria-expanded', 'true'))
    item.addEventListener('focusout', (e) => {
      const next = (e as FocusEvent).relatedTarget as Node | null
      if (!next || !item.contains(next)) trigger.setAttribute('aria-expanded', 'false')
    })
    item.addEventListener('keydown', (e) => {
      const ke = e as KeyboardEvent
      if (ke.key === 'ArrowDown') {
        ke.preventDefault()
        const active = document.activeElement
        const idx = menuItems.indexOf(active as HTMLAnchorElement)
        const next = idx < 0 ? menuItems[0] : menuItems[Math.min(idx + 1, menuItems.length - 1)]
        next?.focus()
      } else if (ke.key === 'ArrowUp') {
        ke.preventDefault()
        const idx = menuItems.indexOf(document.activeElement as HTMLAnchorElement)
        if (idx > 0) menuItems[idx - 1]?.focus()
        else trigger.focus()
      } else if (ke.key === 'Escape') {
        ke.preventDefault()
        trigger.focus()
        trigger.setAttribute('aria-expanded', 'false')
      }
    })
  })

  const cartToggle = document.querySelector<HTMLButtonElement>('[data-cart-toggle]')
  cartToggle?.addEventListener('click', () => {
    window.dispatchEvent(new CustomEvent('meera:cart:open'))
  })

  const countEl = document.querySelector<HTMLElement>('[data-cart-count]')
  window.addEventListener('meera:cart:updated', (evt) => {
    if (!countEl) return
    countEl.textContent = String(evt.detail.itemCount)
    countEl.dataset.cartCount = String(evt.detail.itemCount)
  })

  const announcement = document.querySelector<HTMLElement>('[data-announcement-bar]')
  const dismiss = announcement?.querySelector<HTMLButtonElement>('[data-announcement-dismiss]')
  const STORAGE_KEY = 'meera:announcement-dismissed'

  if (announcement && localStorage.getItem(STORAGE_KEY) === '1') {
    announcement.classList.add('is-dismissed')
  }
  dismiss?.addEventListener('click', () => {
    announcement?.classList.add('is-dismissed')
    localStorage.setItem(STORAGE_KEY, '1')
  })
}
